import type { BuilderState } from "../builder-state";
import { isSafeIdent, NO_CAPS, type ConnectionString, type DialectCapabilities } from "./shared";

export const label = "Oracle";
export const defaultPort = 1521;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  referencesAndTrigger: true,
  quoteIdentifiers: true,
  superuserToggle: true, // GRANT DBA
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (l = "") => out.push(l);
  const c = (t: string) => s.includeComments && push(`-- ${t}`);

  // Oracle identifiers are upper-cased unless quoted. Use uppercased unquoted form
  // when safe; otherwise quote.
  const ident = (v: string) =>
    s.quoteIdentifiers || !isSafeIdent(v) ? `"${v.replace(/"/g, '""')}"` : v.toUpperCase();
  const user = ident(s.userName);
  const tablespace = ident(s.dbName); // dbName is reused as the default tablespace name
  const pwd = s.password || "CHANGE_ME";
  // Oracle passwords in CREATE USER are unquoted identifiers; if they contain
  // anything outside [A-Za-z0-9_] they must be double-quoted.
  const pwdLit = /^[A-Za-z0-9_]+$/.test(pwd) ? pwd : `"${pwd.replace(/"/g, '""')}"`;

  c("============================================================");
  c(`Oracle — ${s.mode === "create" ? "Create" : "Update"} user (schema)`);
  c("Run as SYS/SYSTEM or a DBA. Oracle treats a USER as a SCHEMA.");
  c("In a multitenant container DB (CDB), connect to the target PDB first,");
  c("e.g.  ALTER SESSION SET CONTAINER = <pdb_name>;");
  c("============================================================");
  push();

  if (s.mode === "create") {
    c("---- 1. TABLESPACE ------------------------------------------");
    c("Optional: dedicated tablespace for this user's objects.");
    push(
      `-- CREATE TABLESPACE ${tablespace} DATAFILE '${s.dbName}.dbf' SIZE 100M AUTOEXTEND ON NEXT 50M MAXSIZE UNLIMITED;`,
    );
    push();

    c("---- 2. USER (SCHEMA) ---------------------------------------");
    const parts: string[] = [`CREATE USER ${user} IDENTIFIED BY ${pwdLit}`];
    parts.push(`DEFAULT TABLESPACE ${tablespace}`);
    parts.push(`QUOTA UNLIMITED ON ${tablespace}`);
    if (s.connectionLimit !== -1) {
      // Oracle uses profiles for connection limits; emit a hint.
      c(`Connection limit (${s.connectionLimit}) is enforced via a PROFILE in Oracle.`);
    }
    if (s.validUntil) parts.push(`PASSWORD EXPIRE`);
    push(parts.join("\n  ") + ";");
    push();
  } else {
    c("---- 1. UPDATE USER -----------------------------------------");
    if (s.resetPassword && s.password) {
      push(`ALTER USER ${user} IDENTIFIED BY ${pwdLit};`);
    }
    if (s.validUntil) push(`ALTER USER ${user} PASSWORD EXPIRE;`);
    push();
  }

  c("---- 3. SYSTEM PRIVILEGES -----------------------------------");
  push(`GRANT CREATE SESSION TO ${user};`);
  if (s.privCreateSchema) {
    push(`GRANT CREATE TABLE, CREATE VIEW, CREATE SEQUENCE, CREATE PROCEDURE TO ${user};`);
  }
  if (s.createDb) push(`GRANT CREATE TABLESPACE TO ${user};`);
  if (s.createRole) push(`GRANT CREATE ROLE TO ${user};`);
  if (s.superuser) {
    c("DBA bypasses all security checks. Avoid for application accounts.");
    push(`GRANT DBA TO ${user};`);
  }
  push();

  c("---- 4. OBJECT PRIVILEGES (template) ------------------------");
  c("Oracle grants are per-object. Replace OWNER.OBJECT with real targets,");
  c("or grant via a role. Below is a template you can repeat per table.");
  const tableP: string[] = [];
  if (s.privSelect) tableP.push("SELECT");
  if (s.privInsert) tableP.push("INSERT");
  if (s.privUpdate) tableP.push("UPDATE");
  if (s.privDelete) tableP.push("DELETE");
  if (s.privReferences) tableP.push("REFERENCES");
  if (s.privTrigger) tableP.push("ALTER"); // TRIGGER privilege is via ALTER on Oracle
  if (tableP.length) {
    push(`-- GRANT ${tableP.join(", ")} ON OWNER.TABLE_NAME TO ${user};`);
  }
  if (s.privExecuteFn) push(`-- GRANT EXECUTE ON OWNER.PACKAGE_OR_FUNCTION TO ${user};`);
  push();

  if (s.mode === "update" && s.revokeBeforeGrant) {
    c("Revoke template (mirror of GRANT). Adjust per-object as needed.");
    if (tableP.length) push(`-- REVOKE ${tableP.join(", ")} ON OWNER.TABLE_NAME FROM ${user};`);
    push();
  }

  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const service = encodeURIComponent(s.dbName || "ORCLPDB1");
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    {
      label: "Easy Connect (sqlplus / instant client)",
      url: `${s.userName}/${s.password}@${s.host}:${s.port}/${s.dbName || "ORCLPDB1"}`,
      note: "Easy Connect uses service name (typically the PDB name).",
    },
    {
      label: "JDBC (thin)",
      url: `jdbc:oracle:thin:@//${s.host}:${s.port}/${s.dbName || "ORCLPDB1"}`,
    },
    {
      label: "SQLAlchemy (cx_Oracle / oracledb)",
      url: `oracle+oracledb://${auth}@${s.host}:${s.port}/?service_name=${service}`,
    },
    {
      label: "Node.js (oracledb)",
      url: `${s.host}:${s.port}/${s.dbName || "ORCLPDB1"}`,
      note: "Pass as connectString; user/password are separate parameters.",
    },
  ];
}
